import { Feature } from "ol";
import type { Coordinate } from "ol/coordinate";
import type { FeatureLike } from "ol/Feature";
import { Geometry, Point } from "ol/geom";
import VectorLayer from "ol/layer/Vector";
import Vector from "ol/source/Vector";
import { Circle as CircleStyle, Fill, Stroke, Style } from "ol/style";

/**
 * The "ghost" pointer drawn on the compare pane the mouse is NOT over.
 *
 * Both panes share one synced view (see FieldsScreenVM.setupViewSync), so a map
 * coordinate lands on the same screen position on both sides. Mirroring the
 * pointer — and the outline of whatever cell/zone/swath it is over — is what
 * makes "this hexagon is that hexagon" readable across the split.
 *
 * Two roles share one layer:
 *  - cursor: follows the live pointer, cleared when it leaves the pane.
 *  - pin: the cell pinned by a click on the main map, stays until dismissed.
 */

const KIND = "linkedCursorKind";

const cursorPointStyle = new Style({
  image: new CircleStyle({
    radius: 6,
    fill: new Fill({ color: "rgba(104, 173, 24, 0.35)" }),
    stroke: new Stroke({ color: "#ffffff", width: 2 }),
  }),
});

const pinPointStyle = new Style({
  image: new CircleStyle({
    radius: 6,
    fill: new Fill({ color: "#68ad18" }),
    stroke: new Stroke({ color: "#ffffff", width: 2 }),
  }),
});

// Two strokes per outline: a white halo underneath so it stays visible over dark
// satellite imagery, the brand green on top so it reads over a bright heatmap.
const cursorShapeStyle = [
  new Style({ stroke: new Stroke({ color: "rgba(255,255,255,0.9)", width: 4 }) }),
  new Style({ stroke: new Stroke({ color: "#68ad18", width: 2, lineDash: [6, 4] }) }),
];

const pinShapeStyle = [
  new Style({ stroke: new Stroke({ color: "rgba(255,255,255,0.9)", width: 4 }) }),
  new Style({ stroke: new Stroke({ color: "#68ad18", width: 2 }) }),
];

const styleFor = (feature: FeatureLike): Style | Style[] => {
  switch (feature.get(KIND)) {
    case "cursor":
      return cursorPointStyle;
    case "pin":
      return pinPointStyle;
    case "pinShape":
      return pinShapeStyle;
    default:
      return cursorShapeStyle;
  }
};

const makePoint = (kind: "cursor" | "pin"): Feature<Geometry> => {
  const feature = new Feature<Geometry>(new Point([0, 0]));
  feature.set(KIND, kind);
  return feature;
};

export class LinkedCursorLayer {
  readonly layer: VectorLayer<Vector<Geometry>>;

  private readonly source = new Vector<Geometry>();
  private readonly cursorPoint = makePoint("cursor");
  private readonly pinPoint = makePoint("pin");

  /** Origin features, kept only to skip re-cloning while the pointer stays
   *  inside the same cell. */
  private cursorOrigin: Feature<Geometry> | null = null;
  private cursorShape: Feature<Geometry> | null = null;
  private pinShape: Feature<Geometry> | null = null;

  private cursorVisible = false;
  private pinVisible = false;

  constructor() {
    this.layer = new VectorLayer({
      source: this.source,
      style: styleFor,
      // The ghost must keep up with panning/zooming, otherwise it lags a frame
      // behind the real pointer on the other side.
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    });
  }

  /** Mirror the live pointer. `origin` is the hovered cell on the other pane,
   *  if any — its outline is cloned so the matching area is obvious. */
  showCursor = (coord: Coordinate, origin?: Feature<Geometry> | null) => {
    (this.cursorPoint.getGeometry() as Point).setCoordinates(coord);
    if (!this.cursorVisible) {
      this.source.addFeature(this.cursorPoint);
      this.cursorVisible = true;
    }

    const next = origin ?? null;
    if (next === this.cursorOrigin) return;
    this.cursorOrigin = next;
    if (this.cursorShape) {
      this.source.removeFeature(this.cursorShape);
      this.cursorShape = null;
    }
    const geometry = next?.getGeometry();
    if (!geometry) return;
    this.cursorShape = new Feature<Geometry>(geometry.clone());
    this.cursorShape.set(KIND, "cursorShape");
    this.source.addFeature(this.cursorShape);
  };

  hideCursor = () => {
    if (this.cursorVisible) {
      this.source.removeFeature(this.cursorPoint);
      this.cursorVisible = false;
    }
    if (this.cursorShape) {
      this.source.removeFeature(this.cursorShape);
      this.cursorShape = null;
    }
    this.cursorOrigin = null;
  };

  /** Mirror a pinned cell. Unlike the cursor it survives the pointer leaving. */
  showPin = (coord: Coordinate, origin?: Feature<Geometry> | null) => {
    this.hidePin();
    (this.pinPoint.getGeometry() as Point).setCoordinates(coord);
    this.source.addFeature(this.pinPoint);
    this.pinVisible = true;
    const geometry = origin?.getGeometry();
    if (!geometry) return;
    this.pinShape = new Feature<Geometry>(geometry.clone());
    this.pinShape.set(KIND, "pinShape");
    this.source.addFeature(this.pinShape);
  };

  hidePin = () => {
    if (this.pinVisible) {
      this.source.removeFeature(this.pinPoint);
      this.pinVisible = false;
    }
    if (this.pinShape) {
      this.source.removeFeature(this.pinShape);
      this.pinShape = null;
    }
  };

  hide = () => {
    this.hideCursor();
    this.hidePin();
  };
}
